import React, { useState, useEffect } from 'react';

const getBackendUrl = () => {
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  return isLocalhost ? 'http://localhost:8000' : 'https://gradi.ai';
};

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const ChannelAnalysis = ({ onPageChange }) => {
  const [channelUrl, setChannelUrl] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState(null);

  // Separate function to handle analysis with URL parameter
  const handleAnalysisWithUrl = async (url) => {
    if (!url || !url.trim()) {
      setError('Please enter a channel URL');
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setAnalysisResult(null);

    try {
      const response = await fetch(`${API}/analyze-channel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ channel_url: url })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.detail || data.error || 'Channel analysis failed');
      }

      setAnalysisResult(data.data);
    } catch (err) {
      setError(err.message || 'Failed to analyze channel');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleAnalysis = async () => {
    if (!channelUrl.trim()) {
      setError('Please enter a channel URL');
      return;
    }
    await handleAnalysisWithUrl(channelUrl);
  };

  // Handle channel URL from search/navbar - must be after handleAnalysisWithUrl is defined
  useEffect(() => {
    const handleChannelAnalyze = (event) => {
      const url = event.detail?.url;
      if (url && url.trim()) {
        setChannelUrl(url);
        // Auto-trigger analysis immediately
        handleAnalysisWithUrl(url);
      }
    };

    window.addEventListener('analyze-channel', handleChannelAnalyze);
    
    return () => {
      window.removeEventListener('analyze-channel', handleChannelAnalyze);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 1) return 'Today';
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  // Generate growth chart data from recent videos
  const generateGrowthData = (analysisResult) => {
    if (!analysisResult.recent_videos || analysisResult.recent_videos.length === 0) {
      return null;
    }

    const videos = analysisResult.recent_videos.slice(0, 10).reverse(); // Last 10 videos, oldest first
    const now = new Date();
    
    // Calculate views per video over time
    const viewsData = videos.map((video, index) => {
      const daysAgo = Math.floor((now - new Date(video.published_at)) / (1000 * 60 * 60 * 24));
      return {
        label: daysAgo > 30 ? `${Math.floor(daysAgo / 30)}M` : `${daysAgo}d`,
        views: video.views || 0,
        index: index
      };
    });

    // Calculate engagement metrics
    const avgViews = viewsData.reduce((sum, d) => sum + d.views, 0) / viewsData.length;
    const maxViews = Math.max(...viewsData.map(d => d.views));
    
    return { viewsData, avgViews, maxViews };
  };

  // Render growth chart
  const renderGrowthChart = (data, title, color) => {
    if (!data || data.viewsData.length === 0) return null;

    const chartWidth = 800;
    const chartHeight = 300;
    const padding = { top: 20, right: 20, bottom: 40, left: 60 };
    const graphWidth = chartWidth - padding.left - padding.right;
    const graphHeight = chartHeight - padding.top - padding.bottom;

    const maxValue = data.maxViews || 1;
    const stepX = graphWidth / (data.viewsData.length - 1 || 1);
    const scaleY = graphHeight / maxValue;

    // Generate path for line chart
    const points = data.viewsData.map((d, i) => {
      const x = padding.left + (i * stepX);
      const y = padding.top + graphHeight - (d.views * scaleY);
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
    }).join(' ');

    // Generate area path
    const areaPath = points + ` L ${padding.left + (data.viewsData.length - 1) * stepX} ${padding.top + graphHeight} L ${padding.left} ${padding.top + graphHeight} Z`;

    // Create unique gradient ID
    const gradientId = 'gradient-views-chart';

    return (
      <div className="step-card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
        <h4 style={{
          fontSize: 'clamp(1.1rem, 2.5vw, 1.3rem)',
          fontWeight: 'bold',
          marginBottom: '1rem',
          color: '#e5e7eb'
        }}>
          {title}
        </h4>
        <div style={{ overflowX: 'auto', width: '100%' }}>
          <svg width={chartWidth} height={chartHeight} style={{ minWidth: '100%', maxWidth: '100%' }} viewBox={`0 0 ${chartWidth} ${chartHeight}`}>
            {/* Gradient definition */}
            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor={color} stopOpacity="0.5" />
                <stop offset="100%" stopColor={color} stopOpacity="0" />
              </linearGradient>
            </defs>

            {/* Grid lines */}
            {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
              const y = padding.top + graphHeight - (ratio * graphHeight);
              return (
                <g key={ratio}>
                  <line
                    x1={padding.left}
                    y1={y}
                    x2={padding.left + graphWidth}
                    y2={y}
                    stroke="rgba(139, 92, 246, 0.2)"
                    strokeWidth="1"
                    strokeDasharray="4,4"
                  />
                  <text
                    x={padding.left - 10}
                    y={y + 5}
                    fill="#9ca3af"
                    fontSize="12"
                    textAnchor="end"
                  >
                    {formatNumber(ratio * maxValue)}
                  </text>
                </g>
              );
            })}

            {/* Area fill */}
            <path
              d={areaPath}
              fill={`url(#${gradientId})`}
              opacity={0.3}
            />

            {/* Line */}
            <path
              d={points}
              fill="none"
              stroke={color}
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {/* Data points */}
            {data.viewsData.map((d, i) => {
              const x = padding.left + (i * stepX);
              const y = padding.top + graphHeight - (d.views * scaleY);
              return (
                <g key={i}>
                  <circle
                    cx={x}
                    cy={y}
                    r="4"
                    fill={color}
                    stroke="#fff"
                    strokeWidth="2"
                  />
                  {/* X-axis labels */}
                  {i % Math.ceil(data.viewsData.length / 5) === 0 && (
                    <text
                      x={x}
                      y={chartHeight - padding.bottom + 20}
                      fill="#9ca3af"
                      fontSize="11"
                      textAnchor="middle"
                    >
                      {d.label}
                    </text>
                  )}
                </g>
              );
            })}

            {/* Axes */}
            <line
              x1={padding.left}
              y1={padding.top + graphHeight}
              x2={padding.left + graphWidth}
              y2={padding.top + graphHeight}
              stroke="rgba(139, 92, 246, 0.4)"
              strokeWidth="2"
            />
            <line
              x1={padding.left}
              y1={padding.top}
              x2={padding.left}
              y2={padding.top + graphHeight}
              stroke="rgba(139, 92, 246, 0.4)"
              strokeWidth="2"
            />
          </svg>
        </div>
      </div>
    );
  };

  // Render metric comparison chart
  const renderMetricComparison = (analysisResult) => {
    if (!analysisResult) return null;

    const metrics = [
      { 
        label: 'Subscribers', 
        value: analysisResult.subscriber_count,
        color: '#8b5cf6',
        growth: analysisResult.monthly_subscriber_growth 
      },
      { 
        label: 'Total Views', 
        value: analysisResult.total_views,
        color: '#ec4899',
        growth: analysisResult.monthly_view_growth 
      },
      { 
        label: 'Avg Views/Video', 
        value: analysisResult.avg_views_per_video,
        color: '#8b5cf6',
        growth: null 
      },
      { 
        label: 'Video Count', 
        value: analysisResult.video_count,
        color: '#ec4899',
        growth: null 
      }
    ];

    const maxValue = Math.max(...metrics.map(m => m.value));

    return (
      <div className="step-card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
        <h4 style={{
          fontSize: 'clamp(1.1rem, 2.5vw, 1.3rem)',
          fontWeight: 'bold',
          marginBottom: '1.5rem',
          color: '#e5e7eb'
        }}>
          Channel Metrics Comparison
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {metrics.map((metric, index) => {
            const percentage = (metric.value / maxValue) * 100;
            return (
              <div key={index}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '0.5rem',
                  alignItems: 'center'
                }}>
                  <span style={{ color: '#9ca3af', fontSize: '0.9rem' }}>{metric.label}</span>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span style={{ color: '#fff', fontWeight: '600', fontSize: '0.95rem' }}>
                      {formatNumber(metric.value)}
                    </span>
                    {metric.growth && (
                      <span style={{
                        color: '#ec4899',
                        fontSize: '0.85rem',
                        fontWeight: '600'
                      }}>
                        +{formatNumber(metric.growth)}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{
                  width: '100%',
                  height: '8px',
                  background: 'rgba(139, 92, 246, 0.1)',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${percentage}%`,
                    height: '100%',
                    background: `linear-gradient(90deg, ${metric.color}, ${metric.color}dd)`,
                    borderRadius: '4px',
                    transition: 'width 0.5s ease'
                  }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Generate floating particles
  const generateParticles = () => {
    const particles = [];
    const colors = ['#8b5cf6', '#ec4899']; // Consistent color scheme
    
    for (let i = 0; i < 50; i++) {
      particles.push(
        <div
          key={i}
          className="particle"
          style={{
            width: Math.random() * 4 + 2 + 'px',
            height: Math.random() * 4 + 2 + 'px',
            background: `linear-gradient(45deg, ${colors[Math.floor(Math.random() * 2)]}, transparent)`,
            left: Math.random() * 100 + '%',
            top: Math.random() * 100 + '%',
            animation: `float ${Math.random() * 10 + 10}s ease-in-out infinite`,
            animationDelay: Math.random() * 5 + 's'
          }}
        />
      );
    }
    return particles;
  };

  return (
    <div className="page-container">
      {/* Animated Background Elements */}
      <div className="animated-background" />

      {/* Floating particles */}
      <div className="floating-particles">
        {generateParticles()}
      </div>

      <div className="page-content">
        {/* Hero Section */}
        <div className="hero-section">
          <h1 className="hero-title">
            Channel Analysis
          </h1>
          <p className="hero-subtitle">
            Analyze YouTube channel performance and statistics
          </p>
        </div>

        {/* Input Section */}
        <div className="input-section">
          <div className="input-container">
            <input
              type="text"
              placeholder="Enter YouTube Channel URL..."
              value={channelUrl}
              onChange={(e) => setChannelUrl(e.target.value)}
              className="input-field"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isAnalyzing && channelUrl.trim()) {
                  handleAnalysis();
                }
              }}
            />
            <button
              onClick={handleAnalysis}
              disabled={isAnalyzing || !channelUrl.trim()}
              className="analyze-button"
            >
              {isAnalyzing ? (
                <>
                  <span className="spinner" />
                  Analyzing...
                </>
              ) : (
                'Analyze'
              )}
            </button>
          </div>

          {error && (
            <div className="error-container">
              <p className="error-content">{error}</p>
            </div>
          )}
        </div>

        {analysisResult && (
          <div className="results-container">
            <div style={{ display: 'flex', gap: '2rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
              {analysisResult.thumbnail && (
                <img
                  src={analysisResult.thumbnail}
                  alt={analysisResult.channel_name}
                  style={{
                    width: '120px',
                    height: '120px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '3px solid rgba(139, 92, 246, 0.5)'
                  }}
                />
              )}
              <div style={{ flex: 1 }}>
                <h2 className="hero-title" style={{
                  fontSize: 'clamp(1.5rem, 3vw, 2rem)',
                  marginBottom: '0.5rem'
                }}>
                  {analysisResult.channel_name}
                </h2>
                {analysisResult.custom_url && (
                  <p style={{ color: '#9ca3af', marginBottom: '1rem' }}>
                    {analysisResult.custom_url}
                  </p>
                )}
                {analysisResult.description && (
                  <p style={{ color: '#d4d4d4', lineHeight: '1.6' }}>
                    {analysisResult.description.substring(0, 200)}...
                  </p>
                )}
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1.5rem',
              marginBottom: '2rem'
            }}>
              <div style={{
                background: 'rgba(139, 92, 246, 0.1)',
                border: '2px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '16px',
                padding: '1.5rem',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: '900', color: '#8b5cf6', marginBottom: '0.5rem' }}>
                  {formatNumber(analysisResult.subscriber_count)}
                </div>
                <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Subscribers</div>
              </div>

              <div style={{
                background: 'rgba(236, 72, 153, 0.1)',
                border: '2px solid rgba(236, 72, 153, 0.3)',
                borderRadius: '16px',
                padding: '1.5rem',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: '900', color: '#ec4899', marginBottom: '0.5rem' }}>
                  {formatNumber(analysisResult.total_views)}
                </div>
                <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Total Views</div>
              </div>

              <div style={{
                background: 'rgba(139, 92, 246, 0.1)',
                border: '2px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '16px',
                padding: '1.5rem',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: '900', color: '#8b5cf6', marginBottom: '0.5rem' }}>
                  {formatNumber(analysisResult.video_count)}
                </div>
                <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Videos</div>
              </div>

              <div style={{
                background: 'rgba(236, 72, 153, 0.1)',
                border: '2px solid rgba(236, 72, 153, 0.3)',
                borderRadius: '16px',
                padding: '1.5rem',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: '900', color: '#ec4899', marginBottom: '0.5rem' }}>
                  {formatNumber(analysisResult.avg_views_per_video)}
                </div>
                <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Avg Views/Video</div>
              </div>
            </div>

            {/* Grade and Ranks */}
            {analysisResult.grade && analysisResult.ranks && (
              <div style={{ marginTop: '2rem' }}>
                <h3 className="hero-title" style={{
                  fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)',
                  marginBottom: '1.5rem'
                }}>
                  Channel Grade & Ranks
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: '1rem',
                  marginBottom: '2rem'
                }}>
                  <div style={{
                    background: 'rgba(236, 72, 153, 0.1)',
                    border: '2px solid rgba(236, 72, 153, 0.3)',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    textAlign: 'center'
                  }}>
                    <div style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Grade</div>
                    <div style={{ color: '#ec4899', fontSize: '2rem', fontWeight: '700' }}>
                      {analysisResult.grade}
                    </div>
                  </div>
                  {analysisResult.ranks.sb_rank && (
                    <div style={{
                      background: 'rgba(139, 92, 246, 0.1)',
                      border: '2px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      textAlign: 'center'
                    }}>
                      <div style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '0.5rem' }}>SB Rank</div>
                      <div style={{ color: '#8b5cf6', fontSize: '1.2rem', fontWeight: '700' }}>
                        {analysisResult.ranks.sb_rank}
                      </div>
                    </div>
                  )}
                  {analysisResult.ranks.subscribers_rank && (
                    <div style={{
                      background: 'rgba(139, 92, 246, 0.1)',
                      border: '2px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      textAlign: 'center'
                    }}>
                      <div style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Subscribers Rank</div>
                      <div style={{ color: '#8b5cf6', fontSize: '1.2rem', fontWeight: '700' }}>
                        {analysisResult.ranks.subscribers_rank}
                      </div>
                    </div>
                  )}
                  {analysisResult.ranks.views_rank && (
                    <div style={{
                      background: 'rgba(236, 72, 153, 0.1)',
                      border: '2px solid rgba(236, 72, 153, 0.3)',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      textAlign: 'center'
                    }}>
                      <div style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Views Rank</div>
                      <div style={{ color: '#ec4899', fontSize: '1.2rem', fontWeight: '700' }}>
                        {analysisResult.ranks.views_rank}
                      </div>
                    </div>
                  )}
                  {analysisResult.ranks.education_rank && (
                    <div style={{
                      background: 'rgba(139, 92, 246, 0.1)',
                      border: '2px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      textAlign: 'center'
                    }}>
                      <div style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Education Rank</div>
                      <div style={{ color: '#8b5cf6', fontSize: '1.2rem', fontWeight: '700' }}>
                        {analysisResult.ranks.education_rank}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Estimated Earnings */}
            {analysisResult.estimated_earnings && (
              <div style={{ marginTop: '2rem' }}>
                <h3 className="hero-title" style={{
                  fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)',
                  marginBottom: '1.5rem'
                }}>
                  Estimated Earnings
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '1rem'
                }}>
                  <div style={{
                    background: 'rgba(139, 92, 246, 0.1)',
                    border: '2px solid rgba(139, 92, 246, 0.3)',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    textAlign: 'center'
                  }}>
                    <div style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Monthly Estimated Earnings</div>
                    <div style={{ color: '#8b5cf6', fontSize: '1.5rem', fontWeight: '700' }}>
                      {analysisResult.estimated_earnings.monthly_range}
                    </div>
                  </div>
                  <div style={{
                    background: 'rgba(236, 72, 153, 0.1)',
                    border: '2px solid rgba(236, 72, 153, 0.3)',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    textAlign: 'center'
                  }}>
                    <div style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Yearly Estimated Earnings</div>
                    <div style={{ color: '#ec4899', fontSize: '1.5rem', fontWeight: '700' }}>
                      {analysisResult.estimated_earnings.yearly_range}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Growth Metrics */}
            {analysisResult.monthly_subscriber_growth && (
              <div style={{ marginTop: '2rem' }}>
                <h3 className="hero-title" style={{
                  fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)',
                  marginBottom: '1.5rem'
                }}>
                  Recent Performance (Last 30 Days)
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem'
                }}>
                  <div style={{
                    background: 'rgba(139, 92, 246, 0.1)',
                    border: '2px solid rgba(139, 92, 246, 0.3)',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    textAlign: 'center'
                  }}>
                    <div style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Subscribers Gained</div>
                    <div style={{ color: '#8b5cf6', fontSize: '1.5rem', fontWeight: '700' }}>
                      +{analysisResult.monthly_subscriber_growth}
                    </div>
                  </div>
                  <div style={{
                    background: 'rgba(236, 72, 153, 0.1)',
                    border: '2px solid rgba(236, 72, 153, 0.3)',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    textAlign: 'center'
                  }}>
                    <div style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Views Gained</div>
                    <div style={{ color: '#ec4899', fontSize: '1.5rem', fontWeight: '700' }}>
                      +{analysisResult.monthly_view_growth}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Growth Charts */}
            {analysisResult.recent_videos && analysisResult.recent_videos.length > 0 && (
              <div style={{ marginTop: '2rem' }}>
                <h3 className="hero-title" style={{
                  fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)',
                  marginBottom: '1.5rem'
                }}>
                  Channel Growth Analytics
                </h3>
                
                {/* Views Growth Chart */}
                {(() => {
                  const growthData = generateGrowthData(analysisResult);
                  return growthData && renderGrowthChart(
                    growthData,
                    'Views Trend (Recent Videos)',
                    '#8b5cf6'
                  );
                })()}

                {/* Metric Comparison */}
                {renderMetricComparison(analysisResult)}

                {/* Growth Metrics Summary */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '1rem',
                  marginTop: '1rem'
                }}>
                  {analysisResult.monthly_subscriber_growth && (
                    <div className="step-card" style={{ padding: '1.5rem' }}>
                      <div style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                        Monthly Subscriber Growth
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: '0.5rem'
                      }}>
                        <span style={{ color: '#8b5cf6', fontSize: '2rem', fontWeight: '700' }}>
                          +{formatNumber(analysisResult.monthly_subscriber_growth)}
                        </span>
                        <span style={{ color: '#9ca3af', fontSize: '0.9rem' }}>
                          ({((analysisResult.monthly_subscriber_growth / analysisResult.subscriber_count) * 100).toFixed(2)}%)
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {analysisResult.monthly_view_growth && (
                    <div className="step-card" style={{ padding: '1.5rem' }}>
                      <div style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                        Monthly View Growth
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: '0.5rem'
                      }}>
                        <span style={{ color: '#ec4899', fontSize: '2rem', fontWeight: '700' }}>
                          +{formatNumber(analysisResult.monthly_view_growth)}
                        </span>
                        <span style={{ color: '#9ca3af', fontSize: '0.9rem' }}>
                          ({((analysisResult.monthly_view_growth / analysisResult.total_views) * 100).toFixed(2)}%)
                        </span>
                      </div>
                    </div>
                  )}

                  {analysisResult.avg_views_per_video && (
                    <div className="step-card" style={{ padding: '1.5rem' }}>
                      <div style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                        Content Performance Score
                      </div>
                      <div style={{ color: '#8b5cf6', fontSize: '2rem', fontWeight: '700' }}>
                        {formatNumber(analysisResult.avg_views_per_video)}
                      </div>
                      <div style={{ color: '#9ca3af', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                        Average views per video
                      </div>
                    </div>
                  )}

                  {analysisResult.video_count && (
                    <div className="step-card" style={{ padding: '1.5rem' }}>
                      <div style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                        Publishing Rate
                      </div>
                      <div style={{ color: '#ec4899', fontSize: '2rem', fontWeight: '700' }}>
                        {analysisResult.recent_videos?.length || 0}
                      </div>
                      <div style={{ color: '#9ca3af', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                        Videos in last 30 days
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChannelAnalysis;

